import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { FileText, Clock, CheckCircle, X, Truck, Plus, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import { useMemo } from 'react';
import StatusBadge from './components/StatusBadge.js';
import { isAdminUser, isOpsManager } from './accessControl.js';
import { updateOrderWithAudit } from './utils/orderAudit.js';

const ConfirmedOrders = ({ db, user }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- REJECTION STATE ---
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [orderToReject, setOrderToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // --- HOLD STATE ---
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [orderToHold, setOrderToHold] = useState(null);
  const [holdReason, setHoldReason] = useState('');
  const [otherHoldReason, setOtherHoldReason] = useState('');
  const [isHolding, setIsHolding] = useState(false);

  // --- DISPATCH SCHEDULE STATE ---
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [orderToSchedule, setOrderToSchedule] = useState(null);
  const [scheduleDateOption, setScheduleDateOption] = useState('today');
  const [customScheduleDate, setCustomScheduleDate] = useState('');
  const [scheduleNote, setScheduleNote] = useState('');
  const [scheduleRemarks, setScheduleRemarks] = useState('');
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [isScheduleHistoryOpen, setIsScheduleHistoryOpen] = useState(false);

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  const [itemsPerPage, setItemsPerPage] = useState(() => {
    return Number(sessionStorage.getItem('confirmedOrders_itemsPerPage')) || 10;
  });
  const [currentPage, setCurrentPage] = useState(() => {
    return Number(sessionStorage.getItem('confirmedOrders_currentPage')) || 1;
  });
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState(() => {
    const savedFilters = sessionStorage.getItem('confirmedOrders_filters');
    return savedFilters ? JSON.parse(savedFilters) : {
      startDate: '',
      endDate: '',
      salesPerson: '',
      orderType: '',
      customerName: '',
    };
  });

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('confirmedOrders_filters', JSON.stringify(filters));
    sessionStorage.setItem('confirmedOrders_currentPage', currentPage.toString());
    sessionStorage.setItem('confirmedOrders_itemsPerPage', itemsPerPage.toString());
  }, [filters, currentPage, itemsPerPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const isOpsScheduler = user?.role === 'ops' || isOpsManager(user) || isAdminUser(user);

  const formatDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getScheduleDateFromOption = () => {
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);

    if (scheduleDateOption === 'no_schedule') return '';
    if (scheduleDateOption === 'today') return formatDateInputValue(baseDate);
    if (scheduleDateOption === 'tomorrow') {
      const tomorrow = new Date(baseDate);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return formatDateInputValue(tomorrow);
    }
    if (scheduleDateOption === 'in_2_days') {
      const inTwoDays = new Date(baseDate);
      inTwoDays.setDate(inTwoDays.getDate() + 2);
      return formatDateInputValue(inTwoDays);
    }
    return customScheduleDate;
  };

  const getPresetScheduleDisplayDate = (optionId) => {
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);

    if (optionId === 'tomorrow') {
      baseDate.setDate(baseDate.getDate() + 1);
    }

    if (optionId === 'in_2_days') {
      baseDate.setDate(baseDate.getDate() + 2);
    }

    return formatDate(formatDateInputValue(baseDate));
  };

  const getScheduleOptionForDate = (dateString) => {
    if (!dateString) return 'no_schedule';
    if (dateString === formatDateInputValue(new Date())) return 'today';

    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (dateString === formatDateInputValue(tomorrow)) return 'tomorrow';

    const inTwoDays = new Date();
    inTwoDays.setHours(0, 0, 0, 0);
    inTwoDays.setDate(inTwoDays.getDate() + 2);
    if (dateString === formatDateInputValue(inTwoDays)) return 'in_2_days';

    return 'custom';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '---';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const buildScheduleHistoryEntry = ({ action, date, note, remarks, timestamp }) => ({
    action,
    date: date || '',
    note: note || '',
    remarks: remarks || '',
    updatedAt: timestamp,
    updatedBy: user?.email || user?.name || 'Unknown'
  });

  const isTodayScheduleDate = (dateString) => {
    if (!dateString) return false;
    return dateString === formatDateInputValue(new Date());
  };

  const openScheduleModal = (order) => {
    const todayValue = formatDateInputValue(new Date());
    setOrderToSchedule(order);
    setScheduleDateOption(getScheduleOptionForDate(order.dispatchScheduleDate));
    setCustomScheduleDate(order.dispatchScheduleDate || todayValue);
    setScheduleNote(order.dispatchScheduleNote || 'Tentative');
    setScheduleRemarks(order.dispatchScheduleRemarks || '');
    setIsScheduleHistoryOpen(false);
    setShowScheduleModal(true);
  };

  const resetScheduleModal = () => {
    setShowScheduleModal(false);
    setOrderToSchedule(null);
    setScheduleDateOption('today');
    setCustomScheduleDate('');
    setScheduleNote('Tentative');
    setScheduleRemarks('');
    setIsScheduleHistoryOpen(false);
  };

  const handleScheduleSave = async () => {
    const selectedDate = getScheduleDateFromOption();
    const isNoSchedule = scheduleDateOption === 'no_schedule';

    if (!isNoSchedule && !scheduleNote) {
      alert("Please choose a schedule note.");
      return;
    }
    if (isNoSchedule && !scheduleRemarks.trim()) {
      alert("Please add Schedule Remarks before saving No Schedule.");
      return;
    }

    setIsSavingSchedule(true);
    try {
      const timestamp = new Date().toISOString();
      const nextScheduleNote = isNoSchedule ? '' : scheduleNote;
      const nextScheduleRemarks = scheduleRemarks.trim();
      const nextHistory = [
        ...(orderToSchedule?.dispatchScheduleHistory || []),
        buildScheduleHistoryEntry({
          action: isNoSchedule ? 'reset' : 'saved',
          date: selectedDate,
          note: nextScheduleNote,
          remarks: nextScheduleRemarks,
          timestamp
        })
      ];
      const payload = {
        dispatchScheduleDate: selectedDate || '',
        dispatchScheduleNote: nextScheduleNote,
        dispatchScheduleRemarks: nextScheduleRemarks,
        dispatchScheduleHistory: nextHistory,
        dispatchScheduleUpdatedAt: timestamp,
        updatedAt: timestamp
      };

      if (!orderToSchedule?.dispatchScheduledAt) {
        payload.dispatchScheduledAt = timestamp;
      }

      if (user?.email) {
        payload.dispatchScheduledBy = user.email;
      }

      await updateOrderWithAudit(db, {
        orderId: orderToSchedule.id,
        orderPayload: {
          ...payload,
          updatedBy: user?.displayName || user?.email || 'Unknown',
          updatedByEmail: user?.email || ''
        },
        previousData: orderToSchedule,
        user,
        historyEvent: {
          action: 'schedule_set',
          status: orderToSchedule.status,
          user,
          timestamp,
          remarks: nextScheduleRemarks
        },
        auditFields: {
          scheduleSetAt: timestamp,
          scheduleSetBy: user?.displayName || user?.email || 'Unknown'
        }
      });
      resetScheduleModal();
    } catch (error) {
      console.error("Error saving dispatch schedule: ", error);
      alert("Failed to save dispatch schedule.");
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleHoldSubmit = async () => {
    if (!holdReason) {
      alert("Please select a reason for the hold.");
      return;
    }
    if (holdReason === 'Other' && !otherHoldReason.trim()) {
      alert("Please specify the other reason.");
      return;
    }

    const finalReason = holdReason === 'Other' ? otherHoldReason : holdReason;

    if (!window.confirm(`Are you sure you want to place Order #${orderToHold.orderNumber || orderToHold.id} on hold?`)) return;

    setIsHolding(true);
    try {
      const timestamp = new Date().toISOString();
      await updateOrderWithAudit(db, {
        orderId: orderToHold.id,
        orderPayload: {
        status: 'ops_on_hold',
        opsHoldReason: finalReason,
        updatedAt: timestamp,
        updatedBy: user?.displayName || user?.email || 'Unknown',
        updatedByEmail: user?.email || ''
        },
        previousData: orderToHold,
        user,
        historyEvent: {
          action: 'put_on_hold',
          status: 'ops_on_hold',
          user,
          timestamp,
          remarks: finalReason
        },
        auditFields: {
          holdedAt: timestamp,
          holdedBy: user?.displayName || user?.email || 'Unknown'
        }
      });
      alert("Order placed on hold successfully.");
      setShowHoldModal(false);
      setOrderToHold(null);
      setHoldReason('');
      setOtherHoldReason('');
    } catch (error) {
      alert("Error placing order on hold: " + error.message);
    } finally {
      setIsHolding(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason) {
      alert("Please select a rejection reason.");
      return;
    }
    if (rejectionReason === 'Other' && !otherReason.trim()) {
      alert("Please specify the other reason.");
      return;
    }

    const finalReason = rejectionReason === 'Other' ? otherReason : rejectionReason;

    if (!window.confirm(`Are you sure you want to reject Order #${orderToReject.orderNumber || orderToReject.id}?`)) return;

    setIsRejecting(true);
    try {
      const timestamp = new Date().toISOString();
      await updateOrderWithAudit(db, {
        orderId: orderToReject.id,
        orderPayload: {
        status: 'ops_rejected',
        opsRejectionReason: finalReason,
        updatedAt: timestamp,
        updatedBy: user?.displayName || user?.email || 'Unknown',
        updatedByEmail: user?.email || ''
        },
        previousData: orderToReject,
        user,
        historyEvent: {
          action: 'rejected_by_ops',
          status: 'ops_rejected',
          user,
          timestamp,
          remarks: finalReason
        },
        auditFields: {
          rejectedAt: timestamp,
          rejectedBy: user?.displayName || user?.email || 'Unknown'
        }
      });
      alert("Order rejected successfully.");
      setShowRejectModal(false);
      setOrderToReject(null);
      setRejectionReason('');
      setOtherReason('');
    } catch (error) {
      alert("Error rejecting order: " + error.message);
    } finally {
      setIsRejecting(false);
    }
  };

    // Filter and Sorting logic
    const filteredAndSortedOrders = useMemo(() => {
      let filterableOrders = [...orders];
  
      // Apply filters
      filterableOrders = filterableOrders.filter(order => {
        // Date range filter
        if (filters.startDate || filters.endDate) {
          const orderDate = new Date(order.date || order.createdAt);
          orderDate.setHours(0, 0, 0, 0);

          if (filters.startDate) {
            const start = new Date(filters.startDate);
            start.setHours(0, 0, 0, 0);
            if (orderDate < start) return false;
          }

          if (filters.endDate) {
            const end = new Date(filters.endDate);
            end.setHours(0, 0, 0, 0);
            if (orderDate > end) return false;
          }
        }

      if (filters.salesPerson && order.salesPerson?.toLowerCase().indexOf(filters.salesPerson.toLowerCase()) === -1) return false;
      if (filters.orderType && order.orderType?.toLowerCase() !== filters.orderType.toLowerCase()) return false;
      if (filters.customerName && order.customerName?.toLowerCase().indexOf(filters.customerName.toLowerCase()) === -1) return false;
      return true;
    });

    // Apply sorting
    if (sortColumn) {
      filterableOrders.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        if (sortColumn === 'createdAt') {
            const dateA = new Date(aValue);
            const dateB = new Date(bValue);
            if (sortDirection === 'asc') return dateA - dateB;
            return dateB - dateA;
        } else if (typeof aValue === 'string') {
            if (sortDirection === 'asc') return aValue.localeCompare(bValue);
            return bValue.localeCompare(aValue);
        } else {
            if (sortDirection === 'asc') return aValue - bValue;
            return bValue - aValue;
        }
      });
    }
    return filterableOrders;
  }, [orders, sortColumn, sortDirection, filters]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedOrders.length / itemsPerPage);
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedOrders.slice(startIndex, endIndex);
  }, [filteredAndSortedOrders, currentPage, itemsPerPage]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page on sort change
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortDirection === 'asc' ? ' ▲' : ' ▼';
    }
    return '';
  };

  useEffect(() => {
    setLoading(true);
    // Filter for 'in_que' and 'ops_hold' status for the Orders Que
    const ordersQuery = query(
      collection(db, "orders"), 
      where("status", "in", ["in_que", "ops_hold"]), 
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(ordersQuery, (querySnapshot) => {
      const ordersList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setOrders(ordersList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching confirmed orders in real-time: ", error);
      setLoading(false);
    });

    return () => unsubscribe(); // Cleanup on unmount
  }, [db]);


  const renderStatus = (status) => {
    switch (status) {
      case 'in_que':
        return <StatusBadge status="in_que" />;
      case 'draft':
        return <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> Draft</span>;
      case 'confirmed_order':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '5px' }}><FileText size={14} /> Confirmed Order</span>;
      case 'packing':
        return <span style={{ color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: '5px' }}><FileText size={14} /> Packing</span>;
      case 'shipped':
        return <span style={{ color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Shipped</span>;
      case 'in_transit':
        return <span style={{ color: '#f97316', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> In Transit</span>;
      case 'partially_shipped':
        return <span style={{ color: '#4338ca', display: 'flex', alignItems: 'center', gap: '5px' }}><Truck size={14} /> Partially Shipped</span>;
      case 'delivered':
        return <StatusBadge status="delivered" />;
      case 'canceled':
        return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '5px' }}><X size={14} /> Canceled</span>;
      case 'packed_ready':
      case 'invoice_pending':
      case 'ewaybill_pending':
      case 'invoice_ready':
      case 'ewaybill_complete':
      case 'dc_confirmed':
      case 'qc_pending':
        return <span style={{ color: '#4338ca', display: 'flex', alignItems: 'center', gap: '5px' }}><Clock size={14} /> QC Pending</span>;
      case 'qc_passed':
        return <span style={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: '5px' }}><CheckCircle size={14} /> Confirm Dispatch</span>;
      default:
        return status ? status.replace(/_/g, ' ') : status;
    }
  };

  const formatDate = (dateString) => {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const isOverdueScheduleDate = (dateString) => {
    if (!dateString) return false;

    const scheduledDate = new Date(dateString);
    const today = new Date();
    scheduledDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return scheduledDate < today;
  };

  const renderDispatchSchedule = (order) => {
    if (order.dispatchScheduleDate) {
      const isOverdue = isOverdueScheduleDate(order.dispatchScheduleDate);
      return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: isOverdue ? '#b91c1c' : '#1f2937', fontWeight: '600' }}>
          <Calendar size={14} color={isOverdue ? '#dc2626' : '#2563eb'} />
          <span>{formatDate(order.dispatchScheduleDate)}</span>
        </div>
      );
    }

    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '13px' }}>
        <Clock size={14} color="#9ca3af" />
        <span>No Schedule</span>
      </div>
    );
  };

  const handleCreateMocClick = (orderId, dispatchScheduleDate) => {
    if (!isTodayScheduleDate(dispatchScheduleDate)) {
      alert("Cant create MOC. Please change schedule to today to proceed!");
      return;
    }

    navigate('/ops/moc/new', { state: { orderId } });
  };

  const groupedOrders = useMemo(() => {
    const groups = {};

    paginatedOrders.forEach((order) => {
      const dateKey = order.dispatchScheduleDate || 'Not Set';
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(order);
    });

    return Object.keys(groups)
      .sort((a, b) => {
        if (a === 'Not Set') return -1;
        if (b === 'Not Set') return 1;
        return a.localeCompare(b);
      })
      .map((dateKey) => ({
        dateKey,
        items: groups[dateKey]
      }));
  }, [paginatedOrders]);

  const formatScheduleHeader = (dateKey) => {
    if (!dateKey || dateKey === 'Not Set') return 'Unscheduled / No Schedule';
    return new Date(dateKey).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderOrderRow = (order) => (
    <tr 
      key={order.id} 
      style={{ borderBottom: '1px solid #e5e7eb' }}
      className="clickable-row"
      onClick={() => navigate(`/orders/${order.id}`)}
    >
      <td style={tdStyle}>{formatDate(order.createdAt)}</td>
      <td style={{...tdStyle, textAlign:'left'}}>
        <div style={{fontWeight: 'bold', fontSize: '14px'}}>{order.customerName}</div>
        <div style={{fontSize: '11px', color: '#6b7280'}}>{order.subject?.substring(0, 40) || 'No Subject'}...</div>
      </td>
      <td style={tdStyle}>
        <strong style={{fontSize: '13px'}}>{order.orderNumber || order.id.substring(0, 8).toUpperCase()}</strong>
      </td>
      <td style={tdStyle}>{order.salesPerson || 'N/A'}</td>
      <td style={tdStyle}>{order.orderType}</td>
      <td style={tdStyle}>{order.orderValue ? `₹${order.orderValue.toLocaleString('en-IN')}` : '0'}</td>
      <td
        style={{
          ...tdStyle,
          cursor: isOpsScheduler ? 'pointer' : 'default'
        }}
        onClick={(e) => {
          if (!isOpsScheduler) return;
          e.stopPropagation();
          openScheduleModal(order);
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            padding: '6px 10px',
            borderRadius: '999px',
            border: '1px solid #dbeafe',
            backgroundColor: isOpsScheduler ? '#f8fbff' : 'transparent'
          }}
        >
          {renderDispatchSchedule(order)}
        </div>
      </td>
      <td style={tdStyle}>
        {renderStatus(order.status)}
      </td>
      {user?.role === 'ops' && (
        <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <button 
              onClick={() => handleCreateMocClick(order.id, order.dispatchScheduleDate)}
              title={isTodayScheduleDate(order.dispatchScheduleDate) ? 'Create MOC' : 'Cant create MOC. Please change schedule to today to proceed!'}
              style={{
                backgroundColor: isTodayScheduleDate(order.dispatchScheduleDate) ? '#059669' : '#9ca3af',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'pointer',
                opacity: isTodayScheduleDate(order.dispatchScheduleDate) ? 1 : 0.85,
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <Plus size={14} /> Create MOC
            </button>
          </div>
        </td>
      )}
    </tr>
  );

  useEffect(() => {
    if (scheduleDateOption === 'no_schedule') {
      setScheduleNote('');
      setScheduleRemarks('');
    }
  }, [scheduleDateOption]);
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <h2 style={{ marginBottom: '20px', fontSize: isMobile ? '20px' : '24px' }}>Dispatch Que ({filteredAndSortedOrders.length})</h2>

      {/* Filter Section */}
      <div style={{ ...filterSectionStyle, gap: isMobile ? '10px' : '15px', padding: isMobile ? '12px' : '15px' }}>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>From Date</label>
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>To Date</label>
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Customer</label>
          <input type="text" name="customerName" value={filters.customerName} onChange={handleFilterChange} placeholder="Search Customer" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Salesperson</label>
          <input type="text" name="salesPerson" value={filters.salesPerson} onChange={handleFilterChange} placeholder="Search Salesperson" style={filterInputStyle} />
        </div>
        <div style={{ ...filterInputGroupStyle, minWidth: isMobile ? '120px' : '150px' }}>
          <label style={filterLabelStyle}>Order Type</label>
          <select name="orderType" value={filters.orderType} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            <option value="Sales">Sales</option>
            <option value="Rental">Rental</option>
          </select>
        </div>
      </div>

      {orders.length === 0 ? (
        <p style={{ padding: '30px', backgroundColor: '#fff', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
          No orders found in queue.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          {groupedOrders.map((group) => (
            <div key={`group-${group.dateKey}`}>
              <h3 style={{ fontSize: '15px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                <Calendar size={18} color={group.dateKey === 'Not Set' ? '#9ca3af' : '#2563eb'} />
                {formatScheduleHeader(group.dateKey)}
                <span style={{ fontSize: '12px', backgroundColor: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '12px', marginLeft: '5px' }}>
                  {group.items.length}
                </span>
              </h3>
              <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '800px' : 'auto' }}>
                  <thead style={{ backgroundColor: '#f9fafb' }}>
                    <tr>
                      <th style={thStyle} onClick={() => handleSort('createdAt')} >Date{getSortIcon('createdAt')}</th>
                      <th style={{...thStyle, textAlign:'left'}} onClick={() => handleSort('customerName')}>Customer{getSortIcon('customerName')}</th>
                      <th style={thStyle} onClick={() => handleSort('orderNumber')}>Order Number{getSortIcon('orderNumber')}</th>
                      <th style={thStyle} onClick={() => handleSort('salesPerson')}>Salesperson{getSortIcon('salesPerson')}</th>
                      <th style={thStyle} onClick={() => handleSort('orderType')}>Type{getSortIcon('orderType')}</th>
                      <th style={thStyle} onClick={() => handleSort('orderValue')}>Value{getSortIcon('orderValue')}</th>
                      <th style={thStyle}>Dispatch Schedule</th>
                      <th style={thStyle}>Status</th>
                      {user?.role === 'ops' && <th style={thStyle}>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map(renderOrderRow)}
                    {false && paginatedOrders.map((order) => (
                <tr 
                  key={order.id} 
                  style={{ borderBottom: '1px solid #e5e7eb' }}
                  className="clickable-row"
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <td style={tdStyle}>{formatDate(order.createdAt)}</td>
                 <td style={{...tdStyle, textAlign:'left'}}>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{order.customerName}</div>
                    <div style={{fontSize: '11px', color: '#6b7280'}}>{order.subject?.substring(0, 40) || 'No Subject'}...</div>
              </td>
              <td style={tdStyle}>
                <strong style={{fontSize: '13px'}}>{order.orderNumber || order.id.substring(0, 8).toUpperCase()}</strong>
              </td>
                  <td style={tdStyle}>{order.salesPerson || 'N/A'}</td>
                  <td style={tdStyle}>{order.orderType}</td>
                  <td style={tdStyle}>{order.orderValue ? `₹${order.orderValue.toLocaleString('en-IN')}` : '0'}</td>
                  <td
                    style={{
                      ...tdStyle,
                      cursor: isOpsScheduler ? 'pointer' : 'default'
                    }}
                    onClick={(e) => {
                      if (!isOpsScheduler) return;
                      e.stopPropagation();
                      openScheduleModal(order);
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-flex',
                        padding: '6px 10px',
                        borderRadius: '999px',
                        border: '1px solid #dbeafe',
                        backgroundColor: isOpsScheduler ? '#f8fbff' : 'transparent'
                      }}
                    >
                      {renderDispatchSchedule(order)}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    {renderStatus(order.status)}
                  </td>
                  {user?.role === 'ops' && (
                    <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <button 
                          onClick={() => handleCreateMocClick(order.id, order.dispatchScheduleDate)}
                          title={isTodayScheduleDate(order.dispatchScheduleDate) ? 'Create MOC' : 'Cant create MOC. Please change schedule to today to proceed!'}
                          style={{
                            backgroundColor: isTodayScheduleDate(order.dispatchScheduleDate) ? '#059669' : '#9ca3af',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            opacity: isTodayScheduleDate(order.dispatchScheduleDate) ? 1 : 0.85,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                        >
                          <Plus size={14} /> Create MOC
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Pagination Controls */}
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            padding: '15px', 
            borderTop: '1px solid #e5e7eb',
            gap: isMobile ? '15px' : '0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: '10px', fontSize: '13px', color: '#374151' }}>Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{ padding: '5px 8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={paginationButtonStyle}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#374151' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={paginationButtonStyle}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {showScheduleModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.35)',
            zIndex: 9998
          }}
          onClick={resetScheduleModal}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: isMobile ? '100%' : '420px',
              height: '100%',
              backgroundColor: '#ffffff',
              boxShadow: '-12px 0 28px rgba(15, 23, 42, 0.18)',
              padding: '24px 24px 120px 24px',
              overflowY: 'auto',
              boxSizing: 'border-box'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#111827' }}>Dispatch Schedule</h3>
                <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
                  #{orderToSchedule?.orderNumber || orderToSchedule?.id}
                </p>
              </div>
              <button
                onClick={resetScheduleModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '12px' }}>
                Schedule Date
              </label>
              <div style={{ display: 'grid', gap: '10px' }}>
                {[
                  { id: 'today', label: 'Today' },
                  { id: 'tomorrow', label: 'Tomorrow' },
                  { id: 'in_2_days', label: 'In 2 Days' },
                  { id: 'custom', label: 'Custom' }
                ].map((option) => (
                  <label
                    key={option.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '12px 14px',
                      border: scheduleDateOption === option.id ? '1px solid #60a5fa' : '1px solid #d1d5db',
                      borderRadius: '10px',
                      backgroundColor: scheduleDateOption === option.id ? '#eff6ff' : '#ffffff',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="radio"
                        name="scheduleDateOption"
                        value={option.id}
                        checked={scheduleDateOption === option.id}
                        onChange={(e) => setScheduleDateOption(e.target.value)}
                      />
                      <span style={{ fontSize: '14px', color: '#111827', fontWeight: '600' }}>{option.label}</span>
                    </div>
                    {option.id !== 'custom' && option.id !== 'no_schedule' && (
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {getPresetScheduleDisplayDate(option.id)}
                      </span>
                    )}
                  </label>
                ))}
              </div>

              {scheduleDateOption === 'custom' && (
                <div style={{ marginTop: '12px' }}>
                  <input
                    type="date"
                    value={customScheduleDate}
                    onChange={(e) => setCustomScheduleDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '12px 14px',
                  border: scheduleDateOption === 'no_schedule' ? '1px solid #fca5a5' : '1px solid #fecaca',
                  borderRadius: '10px',
                  backgroundColor: scheduleDateOption === 'no_schedule' ? '#fee2e2' : '#fef2f2',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="radio"
                    name="scheduleDateOption"
                    value="no_schedule"
                    checked={scheduleDateOption === 'no_schedule'}
                    onChange={(e) => setScheduleDateOption(e.target.value)}
                  />
                  <span style={{ fontSize: '14px', color: '#991b1b', fontWeight: '600' }}>No Schedule</span>
                </div>
              </label>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '10px' }}>
                Schedule Note
              </label>
              <select
                value={scheduleNote}
                onChange={(e) => setScheduleNote(e.target.value)}
                disabled={scheduleDateOption === 'no_schedule'}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  backgroundColor: scheduleDateOption === 'no_schedule' ? '#f9fafb' : '#fff'
                }}
              >
                <option value="">Select note...</option>
                <option value="Stock expected">Stock expected</option>
                <option value="Tentative">Tentative</option>
                <option value="Client requested">Client requested</option>
                <option value="Transport planning">Transport planning</option>
                <option value="Material under preparation">Material under preparation</option>
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#374151', marginBottom: '10px' }}>
                Schedule Remarks
              </label>
              <textarea
                value={scheduleRemarks}
                onChange={(e) => setScheduleRemarks(e.target.value)}
                placeholder="Add any schedule context for ops and sales..."
                style={{
                  width: '100%',
                  minHeight: '88px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  backgroundColor: '#fff',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={resetScheduleModal}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#fff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleSave}
                disabled={isSavingSchedule}
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#2563eb',
                  color: '#fff',
                  cursor: isSavingSchedule ? 'not-allowed' : 'pointer',
                  fontWeight: '700',
                  opacity: isSavingSchedule ? 0.7 : 1
                }}
              >
                {isSavingSchedule ? 'Saving...' : 'Save'}
              </button>
            </div>

            <div style={{ marginTop: '24px' }}>
              <button
                type="button"
                onClick={() => setIsScheduleHistoryOpen(prev => !prev)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#f8fafc',
                  cursor: 'pointer',
                  color: '#111827',
                  fontSize: '13px',
                  fontWeight: '700'
                }}
              >
                <span>Schedule History</span>
                <span style={{ color: '#6b7280', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {isScheduleHistoryOpen ? 'Hide' : 'Show'}
                  {isScheduleHistoryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </span>
              </button>

              {isScheduleHistoryOpen && (
                <div
                  style={{
                    marginTop: '10px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    backgroundColor: '#f8fafc',
                    overflow: 'hidden'
                  }}
                >
                  {(orderToSchedule?.dispatchScheduleHistory || []).length === 0 ? (
                    <div style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7280' }}>
                      No schedule history yet.
                    </div>
                  ) : (
                    [...(orderToSchedule?.dispatchScheduleHistory || [])]
                      .slice()
                      .reverse()
                      .map((entry, index) => (
                        <div
                          key={`${entry.updatedAt || 'history'}-${index}`}
                          style={{
                            padding: '14px 16px',
                            borderTop: index === 0 ? 'none' : '1px solid #e5e7eb'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: entry.action === 'reset' ? '#dc2626' : '#1d4ed8' }}>
                              {entry.action === 'reset' ? 'Reset to No Schedule' : 'Schedule Saved'}
                            </span>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                              {formatDateTime(entry.updatedAt)}
                            </span>
                          </div>
                          <div style={{ fontSize: '13px', color: '#374151', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span>Date: {entry.date ? formatDate(entry.date) : 'No Schedule'}</span>
                            <span>Note: {entry.note || '---'}</span>
                            <span>Remarks: {entry.remarks || '---'}</span>
                            <span>By: {entry.updatedBy || 'Unknown'}</span>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* REJECTION MODAL */}
      {showRejectModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '400px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#dc2626' }}>Reject Order</h3>
              <button onClick={() => { setShowRejectModal(false); setOrderToReject(null); setRejectionReason(''); setOtherReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Please select a reason for rejecting Order <strong>#{orderToReject?.orderNumber || orderToReject?.id}</strong>. This will return the order to the Sales team.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {["Short Stock", "Client Cancelled", "Duplicate Order", "PO Mismatch", "Other"].map((reason) => (
                <label key={reason} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="rejectionReason" 
                    value={reason} 
                    checked={rejectionReason === reason} 
                    onChange={(e) => setRejectionReason(e.target.value)} 
                  />
                  {reason}
                </label>
              ))}
              
              {rejectionReason === 'Other' && (
                <textarea 
                  placeholder="Please specify the reason..." 
                  value={otherReason} 
                  onChange={(e) => setOtherReason(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minHeight: '80px', marginTop: '10px', boxSizing: 'border-box' }}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => { setShowRejectModal(false); setOrderToReject(null); setRejectionReason(''); setOtherReason(''); }} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 'bold', color: '#374151' }}>
                Cancel
              </button>
              <button onClick={handleRejectSubmit} disabled={isRejecting} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isRejecting ? <Spinner size={16} color="white" /> : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HOLD MODAL */}
      {showHoldModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '10px', width: '400px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#f59e0b' }}>Hold Order</h3>
              <button onClick={() => { setShowHoldModal(false); setOrderToHold(null); setHoldReason(''); setOtherHoldReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '15px' }}>
              Please select a reason for placing Order <strong>#{orderToHold?.orderNumber || orderToHold?.id}</strong> on hold. It will remain in your queue.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {["Awaiting Stock", "Awaiting Client Confirmation", "Pending Document", "Other"].map((reason) => (
                <label key={reason} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer' }}>
                  <input 
                    type="radio" 
                    name="holdReason" 
                    value={reason} 
                    checked={holdReason === reason} 
                    onChange={(e) => setHoldReason(e.target.value)} 
                  />
                  {reason}
                </label>
              ))}
              
              {holdReason === 'Other' && (
                <textarea 
                  placeholder="Please specify the hold reason..." 
                  value={otherHoldReason} 
                  onChange={(e) => setOtherHoldReason(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', minHeight: '80px', marginTop: '10px', boxSizing: 'border-box' }}
                />
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => { setShowHoldModal(false); setOrderToHold(null); setHoldReason(''); setOtherHoldReason(''); }} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 'bold', color: '#374151' }}>
                Cancel
              </button>
              <button onClick={handleHoldSubmit} disabled={isHolding} style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#f59e0b', color: 'white', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isHolding ? <Spinner size={16} color="white" /> : 'Confirm Hold'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const thStyle = { padding: '12px 15px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', cursor: 'pointer' };
const tdStyle = { padding: '10px 15px', fontSize: '13px', textAlign: 'center' };
const linkStyle = { color: '#1f2937', textDecoration: 'none', display: 'block' };
const paginationButtonStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '5px', backgroundColor: '#f9fafb', cursor: 'pointer', fontSize: '13px' };

const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', minWidth: '150px', flex: '1' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const filterInputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };

export default ConfirmedOrders;
